/**
 * 사진찍기 미션 실행 흐름.
 * 공통 로고 영상 → 첫 프레임 인사말(3초) → 스팟별 촬영 안내 영상 → 카메라 촬영.
 * 촬영 이후의 사진 2초·클리어 영상·완료 처리는 카메라 수명주기와 함께
 * `FunnyPhotoRunner`가 소유한다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MissionIntroVideoScreen from './MissionIntroVideoScreen';
import FunnyPhotoGreetingScreen from './FunnyPhotoGreetingScreen';
import FunnyPhotoRunner from './FunnyPhotoRunner';
import { resolveFunnyPhotoGuideVideoUrl } from './funnyPhotoMedia';

export { resolveFunnyPhotoGuideVideoUrl } from './funnyPhotoMedia';

export const FUNNY_PHOTO_PHASE_LOGO = 'logo_video';
export const FUNNY_PHOTO_PHASE_GREETING = 'greeting';
export const FUNNY_PHOTO_PHASE_GUIDE = 'guide_video';
export const FUNNY_PHOTO_PHASE_CAMERA = 'camera';

/** 촬영 안내 영상의 첫 프레임과 인사 문구를 정지 표시하는 시간. */
export const FUNNY_PHOTO_GREETING_MS = 3000;

/**
 * 단계 순서의 **단일 출처**. 전이를 화면마다 하드코딩하면 한 화면만 빠뜨려도
 * 흐름이 조용히 끊긴다(그때 아이는 멈춘 화면을 보고 있게 된다).
 */
const PHASE_ORDER = [
  FUNNY_PHOTO_PHASE_LOGO,
  FUNNY_PHOTO_PHASE_GREETING,
  FUNNY_PHOTO_PHASE_GUIDE,
  FUNNY_PHOTO_PHASE_CAMERA,
];

/** 다음 단계. 마지막(촬영)에서는 더 나아가지 않는다 — 그 뒤는 러너가 소유한다. */
export function nextFunnyPhotoPhase(phase) {
  const i = PHASE_ORDER.indexOf(phase);
  if (i < 0) return FUNNY_PHOTO_PHASE_GREETING;
  return PHASE_ORDER[Math.min(i + 1, PHASE_ORDER.length - 1)];
}

export function resolveFunnyPhotoLogoVideoUrl(spot, commonIntroVideoUrl = null) {
  if (!spot || spot.mission_type !== 'funny_photo') return null;
  return typeof commonIntroVideoUrl === 'string' && commonIntroVideoUrl.trim()
    ? commonIntroVideoUrl.trim()
    : null;
}

export function initialFunnyPhotoPhase(logoVideoUrl) {
  return logoVideoUrl ? FUNNY_PHOTO_PHASE_LOGO : FUNNY_PHOTO_PHASE_GREETING;
}

/** 지정 시간 뒤 다음 단계로. 언마운트/단계 변경 시 반드시 해제한다. */
function useAutoAdvance(active, ms, onAdvance) {
  useEffect(() => {
    if (!active) return undefined;
    const id = setTimeout(onAdvance, ms);
    return () => clearTimeout(id);
  }, [active, ms, onAdvance]);
}

export default function FunnyPhotoMissionFlow(props) {
  return <FunnyPhotoSession key={props.spot?.id} {...props} />;
}

function FunnyPhotoSession({
  spot, config, commonIntroVideoUrl, memberNickname,
  onComplete, onCancel, onCameraError, onVideoAbort, camera,
  active = true,
}) {
  // 한 고객의 실행 설정은 시작 시 고정한다. 재조회/카메라 재시도로 인트로를 다시 틀지 않는다.
  const [session] = useState(() => ({
    logoVideoUrl: resolveFunnyPhotoLogoVideoUrl(spot, commonIntroVideoUrl),
    guideVideoUrl: resolveFunnyPhotoGuideVideoUrl(spot),
    config,
  }));
  const { logoVideoUrl, guideVideoUrl } = session;
  const [phase, setPhase] = useState(() => initialFunnyPhotoPhase(logoVideoUrl));
  const [greetingReady, setGreetingReady] = useState(!guideVideoUrl);
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const aborted = useRef(false);
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active) {
      aborted.current = false;
      setFailure(null);
      setGreetingReady(!guideVideoUrl);
      setAttempt(value => value + 1);
      setPhase(initialFunnyPhotoPhase(logoVideoUrl));
    }
    wasActive.current = active;
  }, [active, guideVideoUrl, logoVideoUrl]);
  const handleGreetingReady = useCallback(() => setGreetingReady(true), []);
  const handleGreetingAbort = useCallback(reason => {
    if (aborted.current) return;
    aborted.current = true;
    setFailure(reason);
    try { onVideoAbort?.(reason, guideVideoUrl); }
    catch (error) { console.warn('사진 미션 영상 오류 보고 실패', error); }
  }, [onVideoAbort, guideVideoUrl]);

  // 지난 단계에서 늦게 도착한 종료 이벤트는 현재 단계를 넘기지 못한다.
  const advance = useCallback(() => {
    if (!aborted.current) setPhase(current => current === phase ? nextFunnyPhotoPhase(current) : current);
  }, [phase]);

  useAutoAdvance(active && !failure && phase === FUNNY_PHOTO_PHASE_GREETING && greetingReady, FUNNY_PHOTO_GREETING_MS, advance);

  const retry = () => {
    aborted.current = false;
    setFailure(null);
    setGreetingReady(false);
    setAttempt(value => value + 1);
    setPhase(FUNNY_PHOTO_PHASE_GREETING);
  };
  const unavailable = failure || !guideVideoUrl;
  return <>
    {active && unavailable && <div className="oxq-screen fp-video-error" role="alert">
      <h2>안내 영상을 준비하지 못했어요</h2>
      <p>다시 시도하거나 대기 화면으로 돌아가 주세요.</p>
      <div className="fp-video-error-actions">
        {guideVideoUrl && <button type="button" onClick={retry}>다시 시도</button>}
        <button type="button" onClick={onCancel}>대기 화면으로</button>
      </div>
    </div>}
    {active && !unavailable && phase === FUNNY_PHOTO_PHASE_LOGO &&
      <MissionIntroVideoScreen
        ariaLabel="사진찍기 미션 주렁 로고 영상"
        logLabel="사진찍기 주렁 로고 영상"
        videoUrl={logoVideoUrl}
        onFinished={advance}
        onAbort={(reason) => onVideoAbort?.(reason, logoVideoUrl)}
      />
    }
    {!unavailable && phase !== FUNNY_PHOTO_PHASE_CAMERA && <div
      className={!active || phase === FUNNY_PHOTO_PHASE_LOGO ? 'fp-video-preparing' : undefined}
      aria-hidden={!active || phase === FUNNY_PHOTO_PHASE_LOGO ? true : undefined}
      inert={!active || phase === FUNNY_PHOTO_PHASE_LOGO ? '' : undefined}
    >
      <FunnyPhotoGreetingScreen
        key={attempt}
        memberNickname={memberNickname}
        guideVideoUrl={guideVideoUrl}
        playing={active && phase === FUNNY_PHOTO_PHASE_GUIDE}
        onGreetingReady={handleGreetingReady}
        onGreetingAbort={handleGreetingAbort}
        onFinished={advance}
        onAbort={handleGreetingAbort}
        onCancel={onCancel}
      />
    </div>}
    {active && !unavailable && phase === FUNNY_PHOTO_PHASE_CAMERA &&
    <FunnyPhotoRunner
      camera={camera}
      config={session.config}
      onComplete={onComplete}
      onCancel={onCancel}
      onCameraError={onCameraError}
      onVideoAbort={onVideoAbort}
    />
    }
  </>;
}
